import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteValue,
  Chip,
  Skeleton,
  TextField,
} from '@mui/material';
import { useQuery } from 'store/queryHooks';
import { QueryType } from 'store/queryRegistry';

export type AutocompleteStoredProps<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<AutocompleteProps<any, Multiple, DisableClearable, FreeSolo>, 'options' | 'loading' | 'renderInput'> & {
  queryType: QueryType;
  queryParams?: Record<string, any>;
  label?: string;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  filterIds?: Array<number | string>; // New prop to filter out specific IDs
};

export default function AutocompleteStored<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(props: AutocompleteStoredProps<Multiple, DisableClearable, FreeSolo>) {
  const { queryType, queryParams, label, renderInput, filterIds, ...autocompleteProps } = props;
  const { data, isLoading, isError, error } = useQuery(queryType, queryParams);

  // Filter options based on filterIds if provided
  const filteredOptions = filterIds && data 
    ? data.filter((option) => !filterIds.includes(props.getOptionKey(option)))
    : data;

  const renderValue = (values, getItemProps) => !isLoading && values.map((value, index) => {
    /*
    const option = data?.find(option => props.getOptionKey(option) === value);
    const label = props.getOptionLabel(option);
    */
    const itemProps = getItemProps({ index });
    return (
      <Chip
        label={props.getOptionLabel(value)}
        color="primary"  
        variant="outlined" 
        size='small'
        {...itemProps}
        key={itemProps.key}
      />
    )
  });

  const getValue = (): AutocompleteValue<unknown, Multiple, DisableClearable, FreeSolo> => {
    if (autocompleteProps.multiple) {
      return (data?.filter(option => {
        return autocompleteProps.value.includes(props.getOptionKey(option))
      }) || []) as AutocompleteValue<unknown, Multiple, DisableClearable, FreeSolo>;
    } else {
      return (data?.find(option => {
        return props.getOptionKey(option) === autocompleteProps.value;
      }) || null) as AutocompleteValue<unknown, Multiple, DisableClearable, FreeSolo>;
    }
  }

  return (
    <Autocomplete
      renderInput={renderInput ?
        renderInput
        : (params) => <>
          {!isLoading ?
            <TextField {...params} error={isError} helperText={isError ? error?.message : ''} label={label}/>
            :
            <Skeleton variant="rounded" width="100%" height={40} animation="pulse"/>
          }
        </>
      }
      renderValue={ autocompleteProps?.multiple ? renderValue : undefined }
      {...autocompleteProps}
      value={getValue()}
      options={filteredOptions || []}
  />);
}
